import type { PODApi as PODMallAutoOrderApi } from '@mathflat/domain/@entities/PODMall/KMMAutoOrder/api'
import type { PODApi as PODMallCartItemApi } from '@mathflat/domain/@entities/PODMall/CartItem/api'
import { Entity as CartItemEntity } from '@mathflat/domain/@entities/PODMall/CartItem/dto'
import type { PODApi as PODMallCouponApi } from '@mathflat/domain/@entities/PODMall/Coupon/api'
import type { Entity as CouponEntity } from '@mathflat/domain/@entities/PODMall/Coupon/dto'
import type { PODApi as PODMallOrderApi } from '@mathflat/domain/@entities/PODMall/Order/api'
import { Entity as OrderEntity } from '@mathflat/domain/@entities/PODMall/Order/dto'
import type { OrderStatusType } from '@mathflat/domain/@entities/PODMall/Order/type'
import type { PODApi as PODMallPaymentApi } from '@mathflat/domain/@entities/PODMall/Payment/api'
import type { PODApi as PODMallPreOrderApi } from '@mathflat/domain/@entities/PODMall/PreOrder/api'
import type { PodProductApi } from '@mathflat/domain/@entities/PODMall/Product/api'
import { Entity as ProductEntity } from '@mathflat/domain/@entities/PODMall/Product/dto'
import { Entity as ProductDetailEntity } from '@mathflat/domain/@entities/PODMall/ProductDetail/dto'
import type { PODApi as PODMallSettlementApi } from '@mathflat/domain/@entities/PODMall/Settlement/api'
import type { PODMallUserApi } from '@mathflat/domain/@entities/PODMall/User/api'
import { AxiosError } from 'axios'

import podMallApi from '@/api/podMallApi'
import CheckableMap from '@/models/CheckableMap'
import type { ParentPaymentValidationDTO } from '@/page/ParentPayment/service/dto'
import { CartItem } from '@/page/PODMall/pages/PODMallCart/_dto/CartItem'
import { CartItemColl } from '@/page/PODMall/pages/PODMallCart/_dto/CartItemColl'
import commonRepo from '@/repository/common.repo'
import { mixpanelClient } from '@/utils/trackings/mixpanel'
import { couponBenefitMap } from '@/utils/trackings/schema/userProperties.schema'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

class PODMallController implements Controller {
  adapter = makeAdapter({
    from(
      type: 'ParentOrders' | 'DirectOrders',
      order: PODMallOrderApi.Response.DirectOrder | PODMallOrderApi.Response.ParentOrder,
    ) {
      if (type === 'ParentOrders') {
        return new OrderEntity.ParentOrder(order as PODMallOrderApi.Response.ParentOrder)
      } else if (type === 'DirectOrders') {
        return new OrderEntity.DirectOrder(order as PODMallOrderApi.Response.DirectOrder)
      }
      throw new Error(`Unsupported order type: ${type}`)
    },
  })

  // -- Product
  async fetchSignatureWorkbookProductList(params: PodProductApi.Request.SignatureWorkbookProduct) {
    const data = await podMallApi.getSignatureWorkbookProductList(params)
    return {
      data: data.content.map((product) => new ProductEntity.SignatureWorkbookProduct(product)),
      last: data.last,
    }
  }

  // TODO: fetchSignatureWorkbookProductList와 병합 필요
  async fetchKMMProductList(params: PodProductApi.Request.KMMProduct) {
    const data = await podMallApi.getKMMProductList(params)
    return {
      data: data.content
        .map((product) => new ProductEntity.KMMProduct(product))
        .sort((a, b) => a.orderingNumber - b.orderingNumber),
      last: data.last,
    }
  }

  async fetchSignatureWorkbookProductDetail(params: PodProductApi.Request.SignatureWorkbookDetail) {
    const data = await podMallApi.getSignatureWorkbookProductDetail(params)
    return new ProductDetailEntity.ProductDetail(data)
  }

  fetchProduct = async <T extends PodProductApi.Response.Product['type']>(
    params: PodProductApi.Request.Product<T>,
  ) => {
    const data = await podMallApi.getProducts<T>(params)
    return data
  }

  fetchPrintDocumentProduct = async () => {
    const {
      data: { content },
    } = await podMallApi.getProducts<'PRINT_DOCUMENT'>({
      type: 'PRINT_DOCUMENT',
      size: 1,
    })

    if (content.length > 0) {
      return new ProductEntity.PrintDocumentProduct(content[0])
    }

    return null
  }

  fetchExpectedPrice = async (
    productId: PodProductApi.Response.Product['id'],
    params: PodProductApi.Request.ExpectedAmount,
  ) => {
    const { data } = await podMallApi.getExpectedPrice(productId, params)
    return data
  }

  // -- User
  async getUser() {
    return await podMallApi.getUser()
  }

  async postUserDeliveryAddress(payload: PODMallUserApi.Request.UserDeliveryRequest) {
    return await podMallApi.putUserDeliveryAddress(payload)
  }

  // -- CartItem
  async getCartItems(params?: PODMallCartItemApi.Request.CartItemParams) {
    const { cartItems, ...rest } = await podMallApi.getCartItems(params)

    return {
      ...rest,
      cartItemColl: new CartItemColl(
        CheckableMap.fromArr(
          cartItems.map((item) => new CartItem(new CartItemEntity.CartItem(item))),
          'id',
        ),
      ),
    }
  }

  async putCartItem(
    cartItemId: PODMallCartItemApi.Request.CartItemId,
    params:
      | PODMallCartItemApi.Request.SignatureWorkbookCartItemModification
      | PODMallCartItemApi.Request.CustomWorkbookCartItemModification
      | PODMallCartItemApi.Request.PrintDocumentCartItemModification,
  ) {
    const { data } = await podMallApi.putCartItem(cartItemId, params)
    return data
  }

  async postCartItem(
    payload:
      | PODMallCartItemApi.Request.SignatureWorkbookCartItemCreation
      | PODMallCartItemApi.Request.CustomWorkbookCartItemCreation
      | PODMallCartItemApi.Request.PrintDocumentCartItemCreation,
  ) {
    const { id } = await podMallApi.postCartItem(payload)
    return id
  }

  async deleteCartItem(cartItemIds: PODMallCartItemApi.Request.CartItemId[]) {
    return await podMallApi.deleteCartItem(cartItemIds)
  }

  async getCartItemPreview(cartItemId: number) {
    const { data } = await podMallApi.getCartItemPreview(cartItemId)
    return data
  }

  // -- PreOrder
  async getPreOrder(id: PODMallPreOrderApi.Request.PreOrder) {
    return await podMallApi.getPreOrder(id)
  }

  async postPreOrder(payload: PODMallPreOrderApi.Request.PreOrderCreation) {
    return await podMallApi.postPreOrder(payload)
  }

  async putPreOrder(payload: PODMallPreOrderApi.Request.PreOrderModification) {
    return await podMallApi.putPreOrder(payload)
  }

  async getDeliveryLeadTime() {
    return await podMallApi.getDeliveryLeadTime()
  }

  /* Order Start */
  async getOrders(pageQueryString: string) {
    const res = await podMallApi.getOrders(pageQueryString)
    return {
      ...res,
      content: res.content.map(
        (order) => this.adapter.from('DirectOrders', order) as OrderEntity.DirectOrder,
      ),
    }
  }

  async getOrderItems(id: PODMallOrderApi.Request.OrderId) {
    return await podMallApi.getOrderItems(id)
  }

  async cancelOrder(id: PODMallOrderApi.Request.OrderId) {
    return await podMallApi.cancelOrder(id)
  }

  async getOrderItemPreview(orderItemId: number) {
    const { data } = await podMallApi.getOrderItemPreview(orderItemId)
    return data
  }

  async getPrintOption() {
    const { data } = await podMallApi.getPrintOption()
    return { ...data, pixelMm: Number(data.pixelMm) }
  }

  async getParentOrders(params: PODMallOrderApi.Request.ParentOrder) {
    const res = await podMallApi.getParentOrdersV2(params)

    return {
      ...res,
      content: res.content.map(
        (order) => this.adapter.from('ParentOrders', order) as OrderEntity.ParentOrder,
      ),
    }
  }

  async getParentOrderDetail(groupOrderId: PODMallOrderApi.Request.OrderId) {
    return await podMallApi.getParentOrderDetailV2(groupOrderId)
  }
  /* Order End */

  /* Payment Start */
  async postPayment(payload: PODMallPreOrderApi.Request.Payment) {
    return await podMallApi.postPaymentToss(payload)
  }

  async postParentPaymentToss(payload: ParentPaymentValidationDTO) {
    return await podMallApi.postParentPaymentToss(payload)
  }

  async getParentPayment(params: PODMallPaymentApi.Request.ParentPaymentParams) {
    return await podMallApi.getParentPaymentV2(params)
  }

  async postParentPaymentSend(payload: PODMallPaymentApi.Request.ParentPaymentSend) {
    return await podMallApi.sendParentPaymentRequest(payload)
  }

  async postParentPaymentSend_v2(payload: PODMallPaymentApi.Request.ParentPaymentSend_v2) {
    return await podMallApi.sendParentPaymentRequest_v2(payload)
  }

  async resendParentPayment(payload: PODMallPaymentApi.Request.ParentPaymentResendPayload) {
    return await podMallApi.putParentPayment(payload)
  }

  async validatePaymentToken(payload: PODMallPaymentApi.Request.ParentPaymentParams) {
    const { data } = await podMallApi.getCheckPaymentToken(payload)
    return data
  }

  async payParentPaymentToDirect(payload: PODMallPaymentApi.Request.ParentPaymentToDirectPayload) {
    const res = await podMallApi.putParentPaymentToDirect(payload)
    return res.data
  }
  /* Payment End */

  // -- Coupon
  async getCouponByPreOrderId(
    id: PODMallPreOrderApi.Request.PreOrder['id'],
    params?: { code: string } | undefined,
  ) {
    return await podMallApi.getCouponByPreOrderId(id, params)
  }

  async getCoupon(params?: { code?: string } | undefined) {
    const res = await podMallApi.getCoupon(params)
    if (commonRepo.academy?.academyId) {
      mixpanelClient.setGlobalEventProperty({
        academy_프린팅쿠폰목록: res.data.map((coupon) => ({
          쿠폰이름: coupon.name,
          쿠폰ID: coupon.id,
          쿠폰혜택: couponBenefitMap[coupon.benefitType],
          쿠폰혜택값: coupon.benefitValue,
          쿠폰만료일: new Date(coupon.endDate),
          쿠폰활성상태: coupon.usable,
        })),
      })
    }

    return res
  }

  async putUserCoupon(payload: PODMallCouponApi.Request.UserCouponRequest) {
    return await podMallApi.putUserCoupon(payload)
  }

  async postUserCouponByCode(
    userCouponId: CouponEntity.Coupon['id'],
    code: PODMallCouponApi.Request.UserCouponByCodeRequest,
  ) {
    return await podMallApi.postUserCouponByCode(userCouponId, code)
  }

  /* 요금제별 할인 정보 - user */
  async getProductDiscountEvent() {
    try {
      return await podMallApi.getProductDiscountEvent()
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response?.status === 404) {
          return null
        }
      }
      throw e
    }
  }

  /* 요금제별 할인 정보 - preOrder */
  async getProductDiscountEventByPreOrder(preOrderId: PODMallPreOrderApi.Request.PreOrder['id']) {
    try {
      return await podMallApi.getProductDiscountEventByPreOrder(preOrderId)
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response?.status === 404) {
          return null
        }
      }
      throw e
    }
  }

  /* 요금제별 할인 적용 */
  async putProductDiscountEvent(
    preOrderId: string,
    userCouponId: CouponEntity.Coupon['id'] | null,
  ) {
    const params: PODMallPreOrderApi.Request.ProductDiscountEvent = {
      action: userCouponId === null ? 'DISABLE_EVENT' : 'ENABLE_EVENT',
    }
    return await podMallApi.putProductDiscountEvent(preOrderId, params)
  }

  putUserPoint = async (
    preOrderId: PODMallPreOrderApi.Request.PreOrder['id'],
    payload: PODMallPreOrderApi.Request.UserPoint,
  ) => {
    return await podMallApi.putUserPoint(preOrderId, payload)
  }

  /* 정산 관련 Start */
  async getSettlementListByYearMonth(
    yearMonth: PODMallSettlementApi.Request.SettlementListParams['targetYearMonth'],
  ) {
    return await podMallApi
      .getGroupSettlementListByYearMonthV2({
        targetYearMonth: yearMonth,
      })
      .then(({ data }) => data)
  }

  async downloadSettlementListExcel(
    yearMonth: PODMallSettlementApi.Request.SettlementListParams['targetYearMonth'],
  ) {
    return await podMallApi.downloadSettlementListExcel({
      targetYearMonth: yearMonth,
    })
  }

  async registerSettlementInfo(
    params: PODMallSettlementApi.Request.SettlementRegister,
    skipErrorToast?: boolean,
  ) {
    return await podMallApi.registerSettlementInfo(params, skipErrorToast)
  }
  /* 정산 관련 End */

  /**
   * KMM 상장&배너 주문 관련 Start
   */
  async createKMMOrder(payload: PODMallOrderApi.Request.KMMOrderCreation) {
    return await podMallApi.postOrder(payload)
  }
  async confirmPayment(payload: PODMallPaymentApi.Request.KMMPaymentConfirmation) {
    return await podMallApi.postKMMOrderPaymentToss(payload)
  }
  async getKMMOrder(productId: number, statuses?: OrderStatusType[]) {
    return await podMallApi.getKMMOrder(productId, statuses)
  }
  /**
   * KMM 자동주문 조회. 404(신규 사용자) → null 로 정규화하여 미신청 상태로 다룬다.
   */
  async getKMMAutoOrder() {
    try {
      return await podMallApi.getKMMAutoOrder()
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 404) {
        return null
      }
      throw e
    }
  }
  async saveKMMAutoOrderDeliveryAddress(payload: PODMallAutoOrderApi.Request.KMMAutoOrderUpsert) {
    return await podMallApi.saveKMMAutoOrderDeliveryAddress(payload)
  }
  async cancelKMMAutoOrder() {
    return await podMallApi.cancelKMMAutoOrder()
  }
  /* KMM 상장&배너 주문 관련 END */
}

export const podMallController = new PODMallController()
